import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
} from '@fortawesome/free-solid-svg-icons';
import {
  faInstagram,
  faFacebook,
  faTwitter,
  faPinterest,
} from '@fortawesome/free-brands-svg-icons';
import PageLayout from '../components/PageLayout';

const ContactIntro = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto ${(props) => props.theme.spacing.lg};
`;

const ContactMethods = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: ${(props) => props.theme.spacing.md};
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const ContactMethod = styled.div`
  flex: 1;
  min-width: 250px;
  padding: ${(props) => props.theme.spacing.md};
  background-color: ${(props) => props.theme.colors.light};
  border-radius: 8px;
  text-align: center;

  h3 {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  svg {
    color: ${(props) => props.theme.colors.secondary};
  }
`;

const ContactNote = styled.p`
  font-size: 0.9rem;
  color: ${(props) => props.theme.colors.textLight};
  margin-top: ${(props) => props.theme.spacing.xs};
`;

const ContactFormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto ${(props) => props.theme.spacing.lg};

  h3 {
    text-align: center;
    margin-bottom: ${(props) => props.theme.spacing.md};
  }
`;

const ContactForm = styled.form`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${(props) => props.theme.spacing.sm};
`;

const FormGroup = styled.div`
  margin-bottom: ${(props) => props.theme.spacing.sm};

  label {
    display: block;
    margin-bottom: 5px;
    font-weight: 500;
  }

  input,
  select,
  textarea {
    width: 100%;
    padding: 12px 15px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-family: ${(props) => props.theme.fonts.body};
  }

  textarea {
    resize: vertical;
    min-height: 150px;
  }
`;

const SubmitButton = styled.button`
  display: inline-block;
  padding: 12px 30px;
  background-color: ${(props) => props.theme.colors.primary};
  color: ${(props) => props.theme.colors.bgLight};
  border: 2px solid ${(props) => props.theme.colors.primary};
  border-radius: 0;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  cursor: pointer;
  font-size: 0.9rem;
  width: auto;
  margin-top: ${(props) => props.theme.spacing.sm};

  &:hover {
    background-color: transparent;
    color: ${(props) => props.theme.colors.primary};
  }
`;

const SocialContact = styled.div`
  text-align: center;
  padding: ${(props) => props.theme.spacing.md};
  background-color: ${(props) => props.theme.colors.light};
  border-radius: 8px;
`;

const SocialIcons = styled.div`
  display: flex;
  justify-content: center;
  gap: ${(props) => props.theme.spacing.sm};
  margin-top: ${(props) => props.theme.spacing.sm};

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: ${(props) => props.theme.colors.primary};
    color: white;
    transition: all 0.3s ease;

    &:hover {
      background-color: ${(props) => props.theme.colors.secondary};
      color: ${(props) => props.theme.colors.dark};
      transform: translateY(-3px);
    }
  }
`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'general',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // In a real app, you would send this data to your backend/API
    setSubmitted(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: 'general',
      message: '',
    });
  };

  return (
    <PageLayout title="Contact Us">
      <ContactIntro>
        <p>
          We'd love to hear from you. Whether you have a question about our
          flower subscriptions, delivery areas, or anything else, our team is
          ready to answer all your questions.
        </p>
      </ContactIntro>

      <ContactMethods>
        <ContactMethod>
          <h3>
            <FontAwesomeIcon icon={faPhone} /> Phone
          </h3>
          <p>+91 9008789490</p>
          <ContactNote>
            Available Monday to Friday, 9:00 AM to 6:00 PM IST
          </ContactNote>
        </ContactMethod>

        <ContactMethod>
          <h3>
            <FontAwesomeIcon icon={faEnvelope} /> Email
          </h3>
          <p>
            <a href="mailto:hello@curators.studio">hello@curators.studio</a>
          </p>
          <ContactNote>
            We aim to respond to all inquiries within 24 hours
          </ContactNote>
        </ContactMethod>

        <ContactMethod>
          <h3>
            <FontAwesomeIcon icon={faMapMarkerAlt} /> Address
          </h3>
          <p>Curators' Studio</p>
          <p>Sector 47, Gurugram</p>
          <p>Haryana, India</p>
        </ContactMethod>
      </ContactMethods>

      <ContactFormContainer>
        <h3>Send us a Message</h3>
        {submitted ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h4>Thank you for your message!</h4>
            <p>We'll get back to you as soon as possible.</p>
          </div>
        ) : (
          <ContactForm onSubmit={handleSubmit}>
            <FormGroup>
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <label htmlFor="phone">Phone (optional)</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <label htmlFor="subject">Subject</label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
              >
                <option value="general">General Inquiry</option>
                <option value="subscription">Subscription Questions</option>
                <option value="delivery">Delivery Information</option>
                <option value="feedback">Feedback</option>
                <option value="other">Other</option>
              </select>
            </FormGroup>
            <FormGroup>
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                rows="5"
                value={formData.message}
                onChange={handleChange}
                required
              ></textarea>
            </FormGroup>
            <SubmitButton type="submit">Send Message</SubmitButton>
          </ContactForm>
        )}
      </ContactFormContainer>

      <SocialContact>
        <h3>Connect With Us</h3>
        <p>
          Follow us on social media for updates, inspiration, and exclusive
          offers.
        </p>
        <SocialIcons>
          <a href="#">
            <FontAwesomeIcon icon={faInstagram} />
          </a>
          <a href="#">
            <FontAwesomeIcon icon={faFacebook} />
          </a>
          <a href="#">
            <FontAwesomeIcon icon={faTwitter} />
          </a>
          <a href="#">
            <FontAwesomeIcon icon={faPinterest} />
          </a>
        </SocialIcons>
      </SocialContact>
    </PageLayout>
  );
};

export default ContactPage;
